import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, HttpError } from '../api';
import { currentLang, errorText, loadPairs, setLanguage, setTargetLanguage, syncLanguage, t } from '../i18n';
import { go } from '../router';
import { adoptUser } from '../session';
import type { LanguagePairInfo } from '../types';

/**
 * Beta invitation redemption: a single-use link becomes a private
 * username/password account with one target language. The new account starts
 * with an empty home — nothing is shared with other learners.
 */
@customElement('join-view')
export class JoinView extends LitElement {
  @property() token = '';
  @state() private username = '';
  @state() private password = '';
  /** The chosen option's value (`optionValue`): a target code, or a pair id when a target has several pairs. */
  @state() private target = 'ca';
  @state() private pairs: LanguagePairInfo[] = [];
  @state() private busy = false;
  @state() private error = '';
  @state() private invitation: 'checking' | 'valid' | 'dead' | 'retry' = 'checking';

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    void this.checkInvitation();
    // Supported pairs come from the server's language table; if the readout
    // fails the form still works with the default target.
    loadPairs().then(
      (pairs) => {
        if (!pairs.length) return;
        this.pairs = pairs;
        this.pick(this.optionValue(pairs.find((p) => p.interface === currentLang()) ?? pairs[0]));
      },
      () => {},
    );
  }

  private checkInvitation = async () => {
    this.invitation = 'checking';
    this.error = '';
    try {
      await api.invitation(this.token);
      this.invitation = 'valid';
    } catch (err) {
      this.error = errorText(err);
      this.invitation = err instanceof HttpError && ['invitation-not-found', 'invitation-used', 'invitation-expired'].includes(err.code ?? '') ? 'dead' : 'retry';
    }
  };

  /** Does another pair teach the same target (so the option must name its explanation language too)? */
  private shared(p: LanguagePairInfo): boolean {
    return this.pairs.filter((q) => q.target === p.target).length > 1;
  }

  /**
   * An option's value: the target code while it has one pair (so a link or a
   * test that picks by target keeps working), the pair id once it has several.
   */
  private optionValue(p: LanguagePairInfo): string {
    return this.shared(p) ? p.id : p.target;
  }

  private chosen(): LanguagePairInfo | undefined {
    return this.pairs.find((p) => this.optionValue(p) === this.target);
  }

  /**
   * Picking a language also switches the interface to that pair's explanation
   * language — an English learner fills in the rest of this form in Persian.
   */
  private pick(value: string) {
    this.target = value;
    const pair = this.chosen();
    setTargetLanguage(pair?.target ?? value);
    if (pair) setLanguage(pair.interface);
  }

  private label(p: LanguagePairInfo) {
    return t('join.pairOption', {
      target: t(`lang.${p.target}`), explain: t(`lang.${p.explain}`),
      interface: t(`lang.${p.interface}`),
      direction: t(p.interface === 'fa' ? 'join.rtl' : 'join.ltr'),
    });
  }

  private submit = async (e: Event) => {
    e.preventDefault();
    if (this.busy || this.invitation !== 'valid') return;
    this.busy = true;
    this.error = '';
    try {
      const pair = this.chosen();
      const { user } = await api.claim(this.token, this.username.trim(), this.password, { pair: pair?.id, target: pair?.target ?? this.target });
      // Drafts on this device now follow this account (and only this one).
      adoptUser(user);
      // The account's own interface language, before the first screen draws.
      await syncLanguage();
      go('/', true);
    } catch (err) {
      this.error = errorText(err);
      if (err instanceof HttpError && ['invitation-not-found', 'invitation-used', 'invitation-expired'].includes(err.code ?? '')) {
        this.invitation = 'dead';
        this.password = '';
      }
    } finally {
      this.busy = false;
    }
  };

  render() {
    return html`
      <div class="screen narrow account">
        <div class="topbar">
          <img src="/logo-64.png" class="brand-logo" alt="" width="32" height="32" />
          <h1>Eaves</h1>
        </div>
        <img src="/key-art.jpg" class="key-art" alt="" width="840" height="719" />
        <h2>${t('join.title')}</h2>
        ${this.invitation === 'valid' ? html`<p class="muted">${t('join.blurb')}</p>` : ''}
        ${this.invitation === 'valid' ? html`<form @submit=${this.submit}>
          <label>${t('join.username')}
            <input type="text" name="username" autocomplete="username" required minlength="3" maxlength="24"
              .value=${this.username} @input=${(e: Event) => (this.username = (e.target as HTMLInputElement).value)} />
          </label>
          <label>${t('join.password')}
            <input type="password" name="password" autocomplete="new-password" required minlength="10"
              .value=${this.password} @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)} />
          </label>
          <label>${t('join.target')}
            <select name="target" .value=${this.target} @change=${(e: Event) => this.pick((e.target as HTMLSelectElement).value)}>
              ${this.pairs.map((p) => html`<option value=${this.optionValue(p)} ?selected=${this.optionValue(p) === this.target}>${this.label(p)}</option>`)}
            </select>
          </label>
          <p class="muted small">${t('join.pairHint')}</p>
          ${this.error ? html`<p class="error" role="alert">${this.error}</p>` : ''}
          <button type="submit" class="btn primary block" ?disabled=${this.busy}>${this.busy ? t('join.creating') : t('join.create')}</button>
        </form>` : html`${this.invitation === 'checking' ? html`<p role="status">${t('join.checking')}</p>` : html`<p class="error" role="alert">${this.error}</p>`}
          ${this.invitation === 'retry' ? html`<button class="btn" @click=${this.checkInvitation}>${t('common.retry')}</button>` : ''}
          ${this.invitation === 'dead' ? html`<p class="muted">${t('join.deadHelp')}</p><a href="/signin">${t('recover.goSignin')}</a>` : ''}`}
        <p class="muted small privacy-line">${t('privacy.line')}</p>
      </div>
    `;
  }
}
